import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { listWaterSourcesForMap } from '@/api/waterSources';
import { WaterSourceDetailDrawer, type WaterSourceRecord } from '@/components/maps/WaterSourceDetailDrawer';

interface WaterSourceOverlayProps {
  tenantId: number | null | undefined;
  onEdit?: (source: WaterSourceRecord) => void;
  /** Called when the user taps a marker — parent owns the detail drawer. */
  onSourceClick?: (source: WaterSourceRecord) => void;
  /** Bump to refetch after a create/update. */
  refreshKey?: number;
}

// Color per condition
const CONDITION_COLOR: Record<string, string> = {
  GOOD: '#1565c0',
  FAIR: '#f57c00',
  POOR: '#c62828',
  OUT_OF_SERVICE: '#757575',
};

// SVG marker factory
function makeIcon(type: string, condition: string): L.DivIcon {
  const color = CONDITION_COLOR[condition] ?? '#1565c0';
  const isHydrant = type === 'HYDRANT';
  const svg = isHydrant
    ? `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="34" viewBox="0 0 28 34">
        <defs><filter id="sh"><feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-opacity="0.3"/></filter></defs>
        <path d="M14 2C8.48 2 4 6.48 4 12c0 7.5 10 20 10 20s10-12.5 10-20C24 6.48 19.52 2 14 2z"
          fill="${color}" filter="url(#sh)" stroke="white" stroke-width="1.5"/>
        <text x="14" y="16" text-anchor="middle" font-size="11" font-weight="700" fill="white" font-family="system-ui">H</text>
      </svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="34" viewBox="0 0 28 34">
        <defs><filter id="sh"><feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-opacity="0.3"/></filter></defs>
        <path d="M14 2C8.48 2 4 6.48 4 12c0 7.5 10 20 10 20s10-12.5 10-20C24 6.48 19.52 2 14 2z"
          fill="${color}" filter="url(#sh)" stroke="white" stroke-width="1.5"/>
        <text x="14" y="16" text-anchor="middle" font-size="11" font-weight="700" fill="white" font-family="system-ui">P</text>
      </svg>`;

  return L.divIcon({
    html: svg,
    className: '',
    iconSize: [28, 34],
    iconAnchor: [14, 34],
    popupAnchor: [0, -36],
  });
}

export function WaterSourceOverlay({ tenantId, onEdit, onSourceClick, refreshKey }: WaterSourceOverlayProps) {
  const map = useMap();
  const layerGroupRef = useRef<L.LayerGroup | null>(null);
  const [sources, setSources] = useState<WaterSourceRecord[]>([]);
  // Keep internal drawer for when no parent owns it (backward compat).
  const [selected, setSelected] = useState<WaterSourceRecord | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Fetch on mount / tenantId / refreshKey change
  useEffect(() => {
    if (!tenantId) return;
    listWaterSourcesForMap(tenantId)
      .then((data: WaterSourceRecord[]) => setSources(data))
      .catch(() => {/* silently ignore */});
  }, [tenantId, refreshKey]);

  // Render markers
  useEffect(() => {
    if (!map) return;

    // Clear previous layer
    if (layerGroupRef.current) {
      layerGroupRef.current.clearLayers();
    } else {
      layerGroupRef.current = L.layerGroup().addTo(map);
    }

    sources.forEach((src) => {
      const marker = L.marker([src.lat, src.lng], { icon: makeIcon(src.source_type, src.condition) });
      marker.on('click', () => {
        if (onSourceClick) {
          onSourceClick(src);
        } else {
          setSelected(src);
          setDrawerOpen(true);
        }
      });
      marker.bindTooltip(src.name, { permanent: false, direction: 'top', offset: [0, -36] });
      marker.addTo(layerGroupRef.current!);
    });

    return () => {
      layerGroupRef.current?.clearLayers();
    };
  }, [map, sources, onSourceClick]);

  // Remove layer on unmount
  useEffect(() => {
    return () => {
      if (layerGroupRef.current) {
        layerGroupRef.current.remove();
        layerGroupRef.current = null;
      }
    };
  }, []);

  function handleInspected(updated: WaterSourceRecord) {
    setSources((prev) => prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
    setSelected((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
  }

  return (
    <WaterSourceDetailDrawer
      open={drawerOpen}
      onOpenChange={setDrawerOpen}
      source={selected}
      canEdit={Boolean(onEdit)}
      onEdit={onEdit}
      onInspected={handleInspected}
    />
  );
}
